// Mirrors impire-sdk-go/guard/wire.go (main, v0.4.1) — the guardrails
// family as the console's two surfaces speak it: the admin's rule verbs and
// the member's escalation verbs (02-DESIGN/07-guardrails/03-the-guardrails-service.md
// § subjects, § validation at the authoring verb; 01-rules-and-enforcement.md
// § a rule is data; 02-escalation-and-grants.md § the approver). JSON field
// names are the Go file's; a shape that differs is a defect here. A subject
// is built by the library, never inlined. The rule watch is spoken by the
// Guardrails page to follow the rule set, as the admin template admits; the
// open, the deposit read and the consume are an enforcement point's and are
// not spoken here.

import { subject } from "@impire-io/sdk";

import type { Role } from "./identity.ts";

/** SERVICE is the guardrails service's micro service name, under which callers discover its sealing key. */
export const SERVICE = "guard";

/** The family's tokens: what a subject names after `guard`. */
export const Token = {
  rules: "rules",
  escalations: "escalations",
  grant: "grant",
  keys: "keys",
  records: "records",
  /** The required rules, which the admin writes in a solo workspace and the steward in a managed one (impire-auth/wire/relation.go GuardRequired). */
  required: "required",
} as const;

/** The actions. */
export const Action = {
  set: "set",
  get: "get",
  list: "list",
  delete: "delete",
  watch: "watch",
  open: "open",
  resolve: "resolve",
  revoke: "revoke",
  consume: "consume",
} as const;

/**
 * Outcome is what a rule set concludes about an invocation. Precedence is
 * deny > escalate > transform > allow (01-rules-and-enforcement.md §
 * evaluation semantics; decision 0066).
 */
export const Outcome = {
  allow: "allow",
  deny: "deny",
  escalate: "escalate",
  transform: "transform",
} as const;

export type Outcome = (typeof Outcome)[keyof typeof Outcome];

/** OUTCOMES lists the four in the Go file's order. */
export const OUTCOMES: readonly Outcome[] = [
  Outcome.allow,
  Outcome.transform,
  Outcome.escalate,
  Outcome.deny,
];

/** The clear headers every reply of an enforcement point carries (01-rules-and-enforcement.md § the outcome on the wire). */
export const Header = {
  /** `allow`, `deny`, `escalate` or `transform`. */
  outcome: "Impire-Guard-Outcome",
  /** `<id>@<revision>` of the deciding rule, or `none`. */
  rule: "Impire-Guard-Rule",
  /** On a `transform`, each transformer the call passed through, one header each; on a guard-transformer-* refusal the one that failed. */
  transformer: "Impire-Guard-Transformer",
} as const;

/** RULE_NONE is the rule header's value when no rule matched. */
export const RULE_NONE = "none";

/** The error codes the service answers with. */
export const Code = {
  denied: "guard-denied",
  escalated: "guard-escalated",
  ruleFailed: "guard-rule-failed",
  grantConsumed: "guard-grant-consumed",
  grantInvalid: "guard-grant-invalid",
  grantExpired: "guard-grant-expired",
  grantMismatch: "guard-grant-mismatch",
  grantUnavailable: "guard-grant-unavailable",
  /** A transformer a rule named that did not answer: none of the name, an attestation that does not vouch it, a leg past its timeout. */
  transformerUnavailable: "guard-transformer-unavailable",
  /** A transformer that answered an error, a body it could not transform, or a token over the bound. */
  transformerFailed: "guard-transformer-failed",
  badRequest: "bad-request",
  notFound: "not-found",
  conflict: "conflict",
  unauthorized: "unauthorized",
  failed: "failed",
} as const;

/**
 * validID reports whether id may stand where an id does: a subject token that
 * is none of the reserved tokens `list`, `watch` and `keys`.
 */
export function validID(id: string): boolean {
  return subject.validToken(id) && id !== Action.list && id !== Action.watch && id !== Token.keys;
}

/** The rule actions that name a rule. */
export type RuleAction = typeof Action.set | typeof Action.get | typeof Action.delete;

/** ruleSubject builds `guard.rules.<action>.<id>` for set, get and delete. */
export function ruleSubject(action: RuleAction, id: string): string {
  if (!validID(id)) {
    throw new Error(`guard: ${id} is not a rule id`);
  }
  return subject.client(subject.Family.guard, Token.rules, action, id);
}

/** The rule watch's three headers (HeaderSnapshot, HeaderSince, HeaderRevision): the leased watch in this family's names. */
export const WATCH_HEADERS = {
  snapshot: "Impire-Guard-Snapshot",
  since: "Impire-Guard-Since",
  revision: "Impire-Guard-Revision",
} as const;

/** rulesWatchSubject is `guard.rules.watch`: the rule set as a leased watch, the required rules with it. */
export function rulesWatchSubject(): string {
  return subject.client(subject.Family.guard, Token.rules, Action.watch);
}

/** rulesListSubject is `guard.rules.list`. */
export function rulesListSubject(): string {
  return subject.client(subject.Family.guard, Token.rules, Action.list);
}

/**
 * requiredSubject builds `guard.required.<action>.<id>`: a required rule,
 * held beside the workspace's own under `required.<id>` — the steward's to
 * write in a managed workspace, an admin's in a solo one.
 */
export function requiredSubject(action: RuleAction, id: string): string {
  if (!validID(id)) {
    throw new Error(`guard: ${id} is not a rule id`);
  }
  return subject.client(subject.Family.guard, Token.required, action, id);
}

/** requiredListSubject is `guard.required.list`. */
export function requiredListSubject(): string {
  return subject.client(subject.Family.guard, Token.required, Action.list);
}

/** REQUIRED_PREFIX is a required rule's name prefix on the wire: `required.r9@41`. */
export const REQUIRED_PREFIX = "required.";

/** The escalation actions a person speaks; `open` is an enforcement point's. */
export type EscalationAction = typeof Action.get | typeof Action.resolve | typeof Action.revoke;

/**
 * ADDRESS_ADMIN is the address of an escalation whose rule's approver is
 * `admin`: the literal that stands where a principal token does
 * (03-the-guardrails-service.md § subjects). No principal token can equal it.
 */
export const ADDRESS_ADMIN = "admin";

/**
 * validAddress reports whether a may stand where an approver does in
 * `guard.escalations.<approver>.…`: `admin` or a principal token, never an
 * escalation action.
 */
export function validAddress(a: string): boolean {
  return (
    subject.validToken(a) &&
    a !== Action.open &&
    a !== Action.list &&
    a !== Action.get &&
    a !== Action.resolve &&
    a !== Action.revoke &&
    a !== Action.watch &&
    a !== Token.keys
  );
}

/**
 * escalationSubject builds `guard.escalations.<approver>.<action>.<id>` for
 * get, resolve and revoke: every verb a person speaks on an escalation carries
 * the address it was opened to (02-escalation-and-grants.md § the approver).
 */
export function escalationSubject(approver: string, action: EscalationAction, id: string): string {
  if (!validAddress(approver)) {
    throw new Error(`guard: ${approver} is not an approver's address`);
  }
  if (!validID(id)) {
    throw new Error(`guard: ${id} is not an escalation id`);
  }
  return subject.client(subject.Family.guard, Token.escalations, approver, action, id);
}

/** escalationsListSubject builds `guard.escalations.<approver>.list`. */
export function escalationsListSubject(approver: string): string {
  if (!validAddress(approver)) {
    throw new Error(`guard: ${approver} is not an approver's address`);
  }
  return subject.client(subject.Family.guard, Token.escalations, approver, Action.list);
}

/**
 * addressFor is the address an escalation is opened to under approver a,
 * asked by the principal whose token is asker.
 */
export function addressFor(a: Approver, asker: string): string {
  return a === Approver.admin ? ADDRESS_ADMIN : asker;
}

/** addressOf is a record's address: the one the service wrote, or the one its approver and asker give. */
export function addressOf(r: Record_): string {
  return r.address ?? addressFor(r.approver, r.principal.token);
}

/** keysSubject is `guard.keys`. */
export function keysSubject(): string {
  return subject.client(subject.Family.guard, Token.keys);
}

/**
 * selector builds a rule's selector, `workload.<id>.<kind>.<name…>`, in the
 * tokenless family grammar: a selector is not a subject and carries no root
 * (decision 0030) — the enforcement point matches it against the address
 * after the root. A pattern token (`*`, `>`) stands where a wildcard goes.
 */
export function selector(workload: string, ...rest: string[]): string {
  return subject.join(subject.Family.workload, workload, ...rest);
}

/** ruleRef renders `<id>@<revision>`: the rule and the text that decided. */
export function ruleRef(id: string, revision: number): string {
  return `${id}@${revision}`;
}

/** parseRuleRef reads `<id>@<revision>` back; undefined for `none` or a malformed reference. */
export function parseRuleRef(ref: string): { id: string; revision: number } | undefined {
  const at = ref.indexOf("@");
  if (at <= 0) {
    return undefined;
  }
  const revision = Number(ref.slice(at + 1));
  if (!/^\d+$/.test(ref.slice(at + 1)) || !Number.isSafeInteger(revision)) {
    return undefined;
  }
  return { id: ref.slice(0, at), revision };
}

/**
 * A duration in a document is written as Go writes one: `30m`, `24h`,
 * `1h30m`. validDuration reports whether s is one, and positive.
 */
export function validDuration(s: string): boolean {
  return /^(\d+(\.\d+)?(ns|us|µs|ms|s|m|h))+$/.test(s) && !/^(0(\.0+)?(ns|us|µs|ms|s|m|h))+$/.test(s);
}

/** Approver names who may resolve an escalation. */
export const Approver = {
  self: "self",
  admin: "admin",
} as const;

export type Approver = (typeof Approver)[keyof typeof Approver];

/** Escalation is a rule's escalation block, present exactly when the expression can yield `escalate`. */
export interface Escalation {
  approver: Approver;
  /** How long the escalation stays open for an approver, as a Go duration. */
  ttl: string;
  /** How long a grant minted on it may be redeemed, as a Go duration. */
  "grant-ttl": string;
  /** The inputs the approver is shown, as paths into the inputs a rule reads: `args.repository`, `principal.display`. */
  context?: string[];
}

/** TRANSFORMER_SERVICE is the micro service name a transformer registers under, its registration name in META_TRANSFORMER_NAME. */
export const TRANSFORMER_SERVICE = "transformer";

/** META_TRANSFORMER_NAME is the metadata key a transformer advertises its registration name under. */
export const META_TRANSFORMER_NAME = "transformer.name";

/** DEFAULT_TRANSFORM_TIMEOUT is each leg's timeout when the block names none: the service fills it in at write. */
export const DEFAULT_TRANSFORM_TIMEOUT = "5s";

/** TRANSFORM_TIMEOUT_BOUND is the reference bound no rule's leg timeout may exceed; an install may change it. */
export const TRANSFORM_TIMEOUT_BOUND = "1m";

/**
 * Transformer is a rule's transformer block, present exactly when the
 * expression can yield `transform` (01-rules-and-enforcement.md § a rule is
 * data; decision 0066): the registration name of the workload the call is
 * handed to — never a workload id, never a subject — and each leg's timeout.
 */
export interface Transformer {
  name: string;
  /** Each leg's timeout, as a Go duration; DEFAULT_TRANSFORM_TIMEOUT when absent. */
  timeout?: string;
}

/** Rule is the document an admin writes. Its version is the bucket's revision, carried beside it. */
export interface Rule {
  /** A subject token the admin chose; the name every outcome carries. */
  id: string;
  /** A subject pattern in the tenant's tokenless grammar under `workload.>`; wildcards are whole tokens. */
  selector: string;
  /** A CEL program concluding one of the four literals. */
  expression: string;
  escalation?: Escalation;
  transformer?: Transformer;
  /** Off keeps the text and stops the rule deciding. */
  enabled: boolean;
}

/** EntryOp is what a rule entry records: a rule written, or removed. */
export const EntryOp = {
  put: "put",
  delete: "delete",
} as const;

export type EntryOp = (typeof EntryOp)[keyof typeof EntryOp];

/** RuleEntry is a rule with its revision: the reply of set, get and delete, and each list entry. On a delete the rule carries its id and nothing else. */
export interface RuleEntry {
  rule: Rule;
  revision: number;
  op: EntryOp;
  /** A required rule, delivered beside the workspace's own. */
  required?: boolean;
}

/** RulesReply is every rule of the tenant. */
export interface RulesReply {
  rules: RuleEntry[];
}

/**
 * A validation refusal's message is `<field>: <reason>`, the field and the
 * reason verbatim (impire-sdk-go/guard: ValidationError.Error), so that the
 * console shows it beside the field. fieldOf reads the two back; undefined
 * when the message names no field.
 */
export function fieldOf(message: string): { field: string; reason: string } | undefined {
  const m = /^([a-z][a-z.-]*): (.+)$/s.exec(message);
  if (m === null || m[1] === undefined || m[2] === undefined) {
    return undefined;
  }
  return { field: m[1], reason: m[2] };
}

/** The kinds of a principal. */
export const Kind = {
  person: "person",
  app: "app",
  workload: "workload",
} as const;

/** Principal is who asked, as the snapshot and the record carry it: read from the verified carrier, never asserted. */
export interface Principal {
  /** The principal token: a person's derived token, an app's or a workload's id. */
  token: string;
  display?: string;
  role: Role;
  /** person | app | workload */
  kind: string;
}

/** Caller is the immediate caller when it is a workload acting on behalf of the principal. */
export interface Caller {
  workload: string;
  placement?: string;
}

/** OpenRequest is the snapshot an enforcement point records when it escalates: the approver decides from it and nothing live. */
export interface OpenRequest {
  /** The rule and revision that decided. */
  rule: string;
  revision: number;
  subject: string;
  principal: Principal;
  caller?: Caller;
  /** The inputs the rule's escalation block named, by path. */
  context?: Record<string, unknown>;
  digest: string;
  canon: string;
  approver: Approver;
  ttl: string;
  "grant-ttl": string;
}

/** Decision is what an approver concludes. */
export const Decision = {
  granted: "granted",
  denied: "denied",
} as const;

export type Decision = (typeof Decision)[keyof typeof Decision];

/** ResolveRequest is the approver's decision. */
export interface ResolveRequest {
  decision: Decision;
}

/** ResolveReply carries the minted grant on `granted`; nothing on `denied`. */
export interface ResolveReply {
  grant?: string;
  /** RFC 3339. */
  expires?: string;
}

/** Resolution is the record of a decision: who resolved, what, when, and the grant's id and expiry on `granted`. */
export interface Resolution {
  decision: Decision;
  resolver: Principal;
  /** RFC 3339. */
  at: string;
  jti?: string;
  /** RFC 3339. */
  grant_expires?: string;
}

/** Status is where an escalation stands. */
export const Status = {
  open: "open",
  granted: "granted",
  denied: "denied",
  expired: "expired",
  revoked: "revoked",
} as const;

export type Status = (typeof Status)[keyof typeof Status];

/** Revocation is the record of an unredeemed grant revoked. */
export interface Revocation {
  by: Principal;
  /** RFC 3339. */
  at: string;
}

/** Record is one escalation in full: the reply of get, and an entry of list. */
export interface Record_ extends OpenRequest {
  id: string;
  /** Who the escalation is addressed to: the asker's token for `self`, `admin` for `admin`. */
  address?: string;
  /** RFC 3339. */
  opened: string;
  /** RFC 3339. */
  expires: string;
  status: Status;
  resolution?: Resolution;
  revoked?: Revocation;
}

/** EscalationsReply lists escalations, newest first. */
export interface EscalationsReply {
  escalations: Record_[];
}
